"use client";
import { z } from "zod";
import Image from "next/image";
import { useState } from "react";
import axios from "@config/axios";
import { contactUs } from "@assets";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { toast } from "@components/ui/toaster";
import { ContactSchema } from "@schemas/contact";
import { Textarea } from "@components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Send, ArrowRight, User } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

const ContactPage = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm<z.infer<typeof ContactSchema>>({
		defaultValues: {
			name: "",
			email: "",
			message: "",
		},
		mode: "onTouched",
		resolver: zodResolver(ContactSchema),
	});

	const handleSubmit = async (values: z.infer<typeof ContactSchema>) => {
		setIsSubmitting(true);

		try {
			const response = await axios.post("/api/contact/submit", values);

			if (response.status === 200) {
				toast.success("Message sent successfully! We'll get back to you soon.");
				form.reset();
			} else {
				throw new Error("Failed to send message");
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			toast.error("Failed to send message. Please try again later.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<main className="w-full">
			<section className="xs:py-10 relative overflow-hidden bg-linear-to-br from-purple-600 to-purple-800 py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24">
				<div className="absolute inset-0 bg-black/10"></div>
				<div className="absolute top-0 right-0 h-full w-full opacity-20">
					<Image src={contactUs} alt="Contact Us Background" className="h-full w-full object-cover" priority />
				</div>
				<div className="xs:px-4 relative z-20 mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 xl:px-12">
					<motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center sm:text-start">
						<motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="xs:text-3xl xs:mb-4 mb-3 text-2xl font-bold text-white sm:mb-6 sm:text-4xl md:text-5xl lg:mb-8 lg:text-6xl xl:text-6xl">
							Get in Touch
						</motion.h1>
						<motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="xs:text-base xs:max-w-3xl xs:mb-6 mx-auto mb-4 max-w-2xl text-sm text-white/90 sm:mx-0 sm:mb-8 sm:text-lg md:text-xl lg:mb-10 lg:text-2xl">
							We&apos;re here to help and answer any questions you might have. We look forward to hearing from you.
						</motion.p>
						<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="xs:flex-row xs:gap-3 flex flex-col justify-center gap-2 sm:justify-start sm:gap-4">
							<Button className="xs:px-6 xs:py-2.5 xs:text-sm rounded-full bg-white px-4 py-2 text-xs font-medium text-purple-600 hover:bg-gray-100 sm:px-8 sm:py-3 sm:text-base lg:px-10 lg:py-4 lg:text-lg">
								Contact Us
								<ArrowRight className="xs:w-4 xs:h-4 xs:ml-2 ml-1 h-3 w-3 sm:h-5 sm:w-5" />
							</Button>
							<Button variant="outline" className="xs:px-6 xs:py-2.5 xs:text-sm rounded-full border-white bg-transparent px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-purple-600 sm:px-8 sm:py-3 sm:text-base lg:px-10 lg:py-4 lg:text-lg">
								Our Information
							</Button>
						</motion.div>
					</motion.div>
				</div>
			</section>

			<section className="xs:-mt-24 relative z-20 -mt-20 mb-10 sm:-mt-28 md:-mt-32 lg:-mt-36 xl:-mt-40">
				<div className="xs:px-4 mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 xl:px-12">
					<div className="flex items-end justify-end">
						<motion.div
							initial={{ opacity: 0, x: 30, y: 20 }}
							animate={{ opacity: 1, x: 0, y: 0 }}
							transition={{ duration: 0.8, delay: 0.5 }}
							className="xs:rounded-xl xs:p-4 w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-3 shadow-2xl sm:rounded-2xl sm:p-6 md:p-8 lg:rounded-3xl lg:p-10 xl:p-12"
						>
							<h2 className="xs:text-xl xs:mb-4 mb-3 text-lg font-bold text-gray-900 sm:mb-6 sm:text-2xl md:text-3xl lg:mb-8 lg:text-2xl xl:text-3xl">Send us a message</h2>
							<Form {...form}>
								<form onSubmit={form.handleSubmit(handleSubmit)} className="xs:space-y-4 space-y-3 sm:space-y-6">
									<FormField
										control={form.control}
										name="name"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													Name<span className="text-destructive">*</span>
												</FormLabel>
												<FormControl>
													<Input placeholder="Enter your name" startContent={<User size={16} />} {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													Email<span className="text-destructive">*</span>
												</FormLabel>
												<FormControl>
													<Input placeholder="Enter your email" startContent={<Mail size={16} />} {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="message"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													Message<span className="text-destructive">*</span>
												</FormLabel>
												<FormControl>
													<Textarea placeholder="Tell us more about your inquiry..." rows={3} {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<Button type="submit" disabled={isSubmitting} className="xs:py-2.5 xs:text-sm w-full bg-purple-600 py-2 text-xs hover:bg-purple-700 disabled:bg-purple-400 sm:py-3 sm:text-base lg:py-4 lg:text-lg">
										{isSubmitting ? (
											<>
												<div className="xs:w-4 xs:h-4 xs:mr-2 mr-1 h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent sm:h-5 sm:w-5" />
												Sending...
											</>
										) : (
											<>
												<Send className="xs:w-4 xs:h-4 xs:mr-2 mr-1 h-3 w-3 sm:h-5 sm:w-5" />
												Send Message
											</>
										)}
									</Button>
								</form>
							</Form>
						</motion.div>
					</div>
				</div>
			</section>

			{/* <section className="py-6 xs:py-8 sm:py-10 md:py-12 lg:py-16 xl:py-20 bg-gray-50">
				<div className="max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 xl:px-12">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xs:gap-8 sm:gap-10 md:gap-12 lg:gap-16 xl:gap-20">
						<motion.div 
							initial={{ opacity: 0, x: -30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							className="space-y-4 xs:space-y-6 sm:space-y-8 lg:space-y-10"
						>
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.3 }}
							>
								<h2 className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-3 xs:mb-4 sm:mb-6 lg:mb-8">Get in Touch</h2>
								<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-base xl:text-lg text-gray-600 mb-4 xs:mb-6 sm:mb-8 lg:mb-10">
									We&apos;re here to help and answer any questions you might have. We look forward to hearing from you.
								</p>
							</motion.div>

							<div className="space-y-3 xs:space-y-4 sm:space-y-6 lg:space-y-8">
								<motion.div 
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.6, delay: 0.4 }}
									className="flex items-start space-x-2 xs:space-x-3 sm:space-x-4 lg:space-x-5"
								>
									<div className="bg-purple-100 p-1.5 xs:p-2 sm:p-3 lg:p-4 rounded-full flex-shrink-0">
										<Mail className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 text-purple-600" />
									</div>
									<div>
										<h3 className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold text-gray-900">Email</h3>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">support@cognition.com</p>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">info@cognition.com</p>
									</div>
								</motion.div>

								<motion.div 
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.6, delay: 0.5 }}
									className="flex items-start space-x-2 xs:space-x-3 sm:space-x-4 lg:space-x-5"
								>
									<div className="bg-blue-100 p-1.5 xs:p-2 sm:p-3 lg:p-4 rounded-full flex-shrink-0">
										<Phone className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 text-blue-600" />
									</div>
									<div>
										<h3 className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold text-gray-900">Phone</h3>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">+44 7825 362359</p>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">Mon-Fri 9AM-6PM GMT</p>
									</div>
								</motion.div>

								<motion.div 
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.6, delay: 0.6 }}
									className="flex items-start space-x-2 xs:space-x-3 sm:space-x-4 lg:space-x-5"
								>
									<div className="bg-green-100 p-1.5 xs:p-2 sm:p-3 lg:p-4 rounded-full flex-shrink-0">
										<MapPin className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 text-green-600" />
									</div>
									<div>
										<h3 className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold text-gray-900">Office</h3>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">1 Gloucester Dr</p>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">London NW11 6BH</p>
										<p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-sm xl:text-base text-gray-600">United Kingdom</p>
									</div>
								</motion.div>
							</div>
						</motion.div>

						<motion.div 
							initial={{ opacity: 0, x: 30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.4 }}
							className="text-center"
						>
							<motion.h2 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-2 xs:mb-3 sm:mb-4 lg:mb-6"
							>
								Visit Our Office
							</motion.h2>
						
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.8, delay: 0.7 }}
								className="bg-white rounded-lg xs:rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden h-48 xs:h-56 sm:h-64 md:h-72 lg:h-80 xl:h-96 w-full max-w-7xl mx-auto"
							>
								<iframe
									src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.8969668021123!2d-0.1937221!3d51.58845170000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487610a602ac5be5%3A0x2273f917927da63d!2s1%20Gloucester%20Dr%2C%20London%20NW11%206BH%2C%20UK!5e0!3m2!1sen!2sin!4v1756872767421!5m2!1sen!2sin"
									width="600"
									height="600"
									style={{ border: 0 }}
									allowFullScreen
									loading="lazy"
									referrerPolicy="no-referrer-when-downgrade"
									title="Cognition Office Location - 1 Gloucester Dr, London NW11 6BH, UK"
									className="w-full h-full"
								></iframe>
							</motion.div>
						</motion.div>
					</div>
				</div>
			</section> */}
		</main>
	);
};

export default ContactPage;
